import React, { useState, useRef } from 'react';

/**
 * FileUploadField
 * Genanvendelig fil-upload komponent til kontaktformularer.
 * Overholder Regel 12 (maks 5 filer, maks 10 MB/fil, maks 20 MB total, PDF/PNG/JPG/WEBP/GIF).
 */
export default function FileUploadField({
  files = [],
  onFilesChange,
  label = 'Vedhæft evt. 1-5 billeder af dit hus på afstand, hvor man kan se taget',
  idPrefix = 'contact',
  maxFiles = 5,
  accentColor = '#BE0F12'
}) {
  const [error, setError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'application/pdf'];
  const maxSingleFileSize = 10 * 1024 * 1024; // 10 MB
  const maxTotalFileSize = 20 * 1024 * 1024; // 20 MB

  const validateAndAddFiles = (incomingFiles) => {
    setError('');
    const newFilesList = Array.from(incomingFiles || []);
    if (!newFilesList.length) return;

    if (files.length + newFilesList.length > maxFiles) {
      setError(`Du kan maksimalt vedhæfte op til ${maxFiles} billeder i alt.`);
    }

    const combined = [...files, ...newFilesList].slice(0, maxFiles);

    // Valider enkeltstørrelser og typer
    for (const f of combined) {
      if (f.size > maxSingleFileSize) {
        setError(`Filen "${f.name}" overskrider grænsen på 10 MB pr. fil.`);
        return;
      }
      const isAllowed = allowedTypes.includes(f.type) || /\.(jpe?g|png|webp|gif|pdf)$/i.test(f.name);
      if (!isAllowed) {
        setError(`Filen "${f.name}" har et ugyldigt format. Tilladte formater er JPG, PNG, WEBP, GIF og PDF.`);
        return;
      }
    }

    // Valider samlet størrelse
    const totalSize = combined.reduce((sum, f) => sum + f.size, 0);
    if (totalSize > maxTotalFileSize) {
      setError('De samlede filer overskrider grænsen på 20 MB.');
      return;
    }

    if (onFilesChange) {
      onFilesChange(combined);
    }
  };

  const handleInputChange = (e) => {
    validateAndAddFiles(e.target.files);
    e.target.value = ''; // Nulstil så samme fil kan vælges igen ved fejl
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (e.dataTransfer?.files) {
      validateAndAddFiles(e.dataTransfer.files);
    }
  };

  const removeFile = (indexToRemove) => {
    setError('');
    const updated = files.filter((_, idx) => idx !== indexToRemove);
    if (onFilesChange) {
      onFilesChange(updated);
    }
  };

  const inputId = `${idPrefix}-file-upload`;

  return (
    <div className="space-y-2">
      {label && (
        <label htmlFor={inputId} className="block text-xs font-bold uppercase tracking-wider text-stone-700">
          {label}
        </label>
      )}

      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`border-2 border-dashed transition-colors p-4 text-center ${
          isDragging
            ? 'border-stone-900 bg-stone-100'
            : 'border-stone-300 bg-stone-50/70 hover:border-stone-400'
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          id={inputId}
          name="billeder"
          multiple
          accept=".pdf,.png,.jpg,.jpeg,.gif,.webp"
          onChange={handleInputChange}
          disabled={files.length >= maxFiles}
          className="sr-only"
        />

        <div className="flex flex-col items-center justify-center gap-1.5">
          <label
            htmlFor={inputId}
            className={`inline-flex items-center gap-2 px-4 py-2 text-xs font-bold uppercase tracking-wider transition-colors select-none ${
              files.length >= maxFiles
                ? 'bg-stone-200 text-stone-400 cursor-not-allowed'
                : 'bg-stone-900 hover:bg-black text-white cursor-pointer shadow-sm'
            }`}
          >
            <i className="fa-solid fa-cloud-arrow-up" aria-hidden="true"></i>
            <span>{files.length >= maxFiles ? `Maks ${maxFiles} billeder valgt` : 'Vælg billeder'}</span>
          </label>

          <p className="text-[11px] text-stone-500">
            Eller træk og slip dine filer her (maks. {maxFiles} filer, op til 10 MB pr. fil)
          </p>
        </div>
      </div>

      {error && (
        <p className="text-xs text-red-600 font-semibold flex items-center gap-1.5" role="alert">
          <i className="fa-solid fa-circle-exclamation" aria-hidden="true"></i>
          <span>{error}</span>
        </p>
      )}

      {files.length > 0 && (
        <div className="space-y-1.5 pt-1">
          <div className="flex items-center justify-between text-[11px] font-bold text-stone-700 uppercase tracking-wide">
            <span>Valgte filer ({files.length}/{maxFiles}):</span>
            <button
              type="button"
              onClick={() => onFilesChange && onFilesChange([])}
              className="text-stone-400 hover:text-red-600 text-[10px] normal-case font-normal underline cursor-pointer"
            >
              Fjern alle
            </button>
          </div>

          <ul className="space-y-1">
            {files.map((file, idx) => {
              const sizeInMb = (file.size / (1024 * 1024)).toFixed(1);
              const isPdf = file.type === 'application/pdf' || file.name.endsWith('.pdf');
              return (
                <li
                  key={`${file.name}-${idx}`}
                  className="flex items-center justify-between bg-white border border-stone-200 px-3 py-1.5 text-xs text-stone-800"
                >
                  <span className="flex items-center gap-2 truncate max-w-[280px] sm:max-w-sm font-medium">
                    <i
                      className={`${isPdf ? 'fa-solid fa-file-pdf text-red-600' : 'fa-solid fa-image text-stone-600'} shrink-0`}
                      aria-hidden="true"
                    />
                    <span className="truncate">{file.name}</span>
                    <span className="text-stone-400 text-[10px] shrink-0 font-normal">({sizeInMb} MB)</span>
                  </span>
                  <button
                    type="button"
                    onClick={() => removeFile(idx)}
                    className="text-stone-400 hover:text-red-600 p-1 ml-2 transition-colors cursor-pointer"
                    aria-label={`Fjern ${file.name}`}
                    title="Fjern fil"
                  >
                    <i className="fa-solid fa-xmark text-sm" aria-hidden="true"></i>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
